import { supabase } from '@/integrations/supabase/client';
import { format, addWeeks, addDays, startOfWeek } from 'date-fns';
import { DuplicationConfig } from '@/components/schedule/DuplicateScheduleDialog';
import { getUserGymId } from '@/utils/gymAuth';
import i18n from '@/i18n';

export const useDuplicateSchedule = () => {
  const duplicateSchedule = async (config: DuplicationConfig): Promise<void> => {
    if (config.type === 'daily') {
      await duplicateDaily(config);
    } else {
      await duplicateWeekly(config);
    }
  };

  const duplicateDaily = async (config: DuplicationConfig): Promise<void> => {
    const gymId = getUserGymId();
    if (!config.sourceDate || !config.targetDates?.length) {
      throw new Error('Source date and target dates are required');
    }

    const sourceDateStr = format(config.sourceDate, 'yyyy-MM-dd');

    // Fetch source day classes
    let sourceQuery = supabase
      .from('scheduled_classes')
      .select('*')
      .eq('gym_id', gymId)
      .eq('class_date', sourceDateStr) as any;

    if (config.calendarId) {
      sourceQuery = sourceQuery.eq('calendar_id', config.calendarId);
    }

    const { data: sourceClasses, error: fetchError } = await sourceQuery as { data: any[] | null; error: any };
    if (fetchError) throw fetchError;
    if (!sourceClasses || sourceClasses.length === 0) {
      throw new Error(i18n.t('duplicateSchedule.noClassesSourceDay', { ns: 'bookingui' }));
    }

    const classesToCreate: any[] = [];
    const classesToUpdate: { id: string; data: any }[] = [];
    const classesToDelete: string[] = [];

    for (const targetDateStr of config.targetDates) {
      // Fetch existing classes for target date
      let existingQuery = supabase
        .from('scheduled_classes')
        .select('*')
        .eq('gym_id', gymId)
        .eq('class_date', targetDateStr);

      if (config.calendarId) {
        existingQuery = existingQuery.eq('calendar_id', config.calendarId);
      }

      const { data: existingClasses } = await existingQuery;

      for (const sourceClass of sourceClasses) {
        const targetCalendarId = config.calendarId ?? sourceClass.calendar_id ?? null;
        const existingClass = existingClasses?.find(cls =>
          cls.time === sourceClass.time &&
          cls.calendar_id === targetCalendarId
        );

        const newClassData = {
          name: sourceClass.name,
          instructor: sourceClass.instructor,
          time: sourceClass.time,
          duration: sourceClass.duration,
          capacity: sourceClass.capacity,
          category_id: sourceClass.category_id,
          course_id: sourceClass.course_id,
          class_date: targetDateStr,
          gym_id: gymId,
          calendar_id: targetCalendarId
        };

        if (existingClass) {
          const isDifferent =
            existingClass.name !== newClassData.name ||
            existingClass.instructor !== newClassData.instructor ||
            existingClass.duration !== newClassData.duration ||
            existingClass.capacity !== newClassData.capacity ||
            existingClass.course_id !== newClassData.course_id;

          if (isDifferent && (config.mode === 'replace' || config.overwriteExisting)) {
            classesToUpdate.push({ id: existingClass.id, data: newClassData });
          }
        } else {
          classesToCreate.push(newClassData);
        }
      }

      if (config.mode === 'replace' && existingClasses) {
        const sourceCalendarIds = new Set(
          sourceClasses.map(sc => config.calendarId ?? sc.calendar_id)
        );
        for (const existingClass of existingClasses) {
          if (!sourceCalendarIds.has(existingClass.calendar_id)) continue;
          const hasSource = sourceClasses.some(sc =>
            sc.time === existingClass.time &&
            (config.calendarId ?? sc.calendar_id) === existingClass.calendar_id
          );
          if (!hasSource) {
            classesToDelete.push(existingClass.id);
          }
        }
      }
    }

    await executeOperations(classesToCreate, classesToUpdate, classesToDelete);
  };

  const duplicateWeekly = async (config: DuplicationConfig): Promise<void> => {
    const gymId = getUserGymId();

    const sourceWeekStart = startOfWeek(config.sourceWeek, { weekStartsOn: 1 });
    const sourceWeekEnd = addDays(sourceWeekStart, 6);

    let sourceQuery = supabase
      .from('scheduled_classes')
      .select('*')
      .eq('gym_id', gymId)
      .gte('class_date', format(sourceWeekStart, 'yyyy-MM-dd'))
      .lte('class_date', format(sourceWeekEnd, 'yyyy-MM-dd')) as any;

    if (config.calendarId) {
      sourceQuery = sourceQuery.eq('calendar_id', config.calendarId);
    }

    const { data: sourceClasses, error: fetchError } = await sourceQuery as { data: any[] | null; error: any };
    if (fetchError) throw fetchError;
    if (!sourceClasses || sourceClasses.length === 0) {
      throw new Error(i18n.t('duplicateSchedule.noClassesSourceWeek', { ns: 'bookingui' }));
    }

    const dayMapping: { [key: string]: number } = {
      'monday': 1, 'tuesday': 2, 'wednesday': 3, 'thursday': 4,
      'friday': 5, 'saturday': 6, 'sunday': 0
    };

    const selectedDayNumbers = config.selectedDays.map(day => dayMapping[day]);
    const filteredClasses = sourceClasses.filter(cls => {
      const dayOfWeek = new Date(cls.class_date).getDay();
      return selectedDayNumbers.includes(dayOfWeek);
    });

    const classesToCreate: any[] = [];
    const classesToUpdate: { id: string; data: any }[] = [];
    const classesToDelete: string[] = [];

    for (let week = 0; week < config.numberOfWeeks; week++) {
      const targetWeekStart = addWeeks(config.targetStartWeek, week);
      const targetWeekEnd = addDays(targetWeekStart, 6);

      let existingQuery = supabase
        .from('scheduled_classes')
        .select('*')
        .eq('gym_id', gymId)
        .gte('class_date', format(targetWeekStart, 'yyyy-MM-dd'))
        .lte('class_date', format(targetWeekEnd, 'yyyy-MM-dd'));

      if (config.calendarId) {
        existingQuery = existingQuery.eq('calendar_id', config.calendarId);
      }

      const { data: existingClasses } = await existingQuery;

      for (const sourceClass of filteredClasses) {
        const sourceDateObj = new Date(sourceClass.class_date);
        const dayOffset = sourceDateObj.getDay() === 0 ? 6 : sourceDateObj.getDay() - 1;
        const targetDate = addDays(targetWeekStart, dayOffset);
        const targetDateStr = format(targetDate, 'yyyy-MM-dd');
        const targetCalendarId = config.calendarId ?? sourceClass.calendar_id ?? null;

        const existingClass = existingClasses?.find(cls =>
          cls.class_date === targetDateStr &&
          cls.time === sourceClass.time &&
          cls.calendar_id === targetCalendarId
        );

        const newClassData = {
          name: sourceClass.name,
          instructor: sourceClass.instructor,
          time: sourceClass.time,
          duration: sourceClass.duration,
          capacity: sourceClass.capacity,
          category_id: sourceClass.category_id,
          course_id: sourceClass.course_id,
          class_date: targetDateStr,
          gym_id: gymId,
          calendar_id: targetCalendarId
        };

        if (existingClass) {
          const isDifferent =
            existingClass.name !== newClassData.name ||
            existingClass.instructor !== newClassData.instructor ||
            existingClass.duration !== newClassData.duration ||
            existingClass.capacity !== newClassData.capacity ||
            existingClass.course_id !== newClassData.course_id;

          if (isDifferent && (config.mode === 'replace' || config.overwriteExisting)) {
            classesToUpdate.push({ id: existingClass.id, data: newClassData });
          }
        } else {
          classesToCreate.push(newClassData);
        }
      }

      if (config.mode === 'replace' && existingClasses) {
        const sourceCalendarIds = new Set(
          filteredClasses.map(sc => config.calendarId ?? sc.calendar_id)
        );
        for (const existingClass of existingClasses) {
          if (!sourceCalendarIds.has(existingClass.calendar_id)) continue;
          const dayOfWeek = new Date(existingClass.class_date).getDay();
          if (selectedDayNumbers.includes(dayOfWeek)) {
            const hasSourceClass = filteredClasses.some(sourceClass => {
              const sourceDate = new Date(sourceClass.class_date);
              const sourceDayOffset = sourceDate.getDay() === 0 ? 6 : sourceDate.getDay() - 1;
              const targetDayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
              return sourceDayOffset === targetDayOffset &&
                sourceClass.time === existingClass.time &&
                (config.calendarId ?? sourceClass.calendar_id) === existingClass.calendar_id;
            });
            if (!hasSourceClass) {
              classesToDelete.push(existingClass.id);
            }
          }
        }
      }
    }

    await executeOperations(classesToCreate, classesToUpdate, classesToDelete);
  };

  const executeOperations = async (
    classesToCreate: any[],
    classesToUpdate: { id: string; data: any }[],
    classesToDelete: string[]
  ) => {
    const operations = [];

    // Les écritures ne portaient aucun filtre de salle : un identifiant
    // provenant d'une autre salle aurait été supprimé ou réécrit si les
    // politiques RLS le laissaient passer. On borne explicitement.
    const gymId = getUserGymId();
    if (!gymId) {
      throw new Error(i18n.t('common.noGymSelected', { ns: 'bookingui' }));
    }

    if (classesToDelete.length > 0) {
      operations.push(
        supabase.from('scheduled_classes').delete().eq('gym_id', gymId).in('id', classesToDelete)
      );
    }

    for (const classToUpdate of classesToUpdate) {
      operations.push(
        supabase.from('scheduled_classes').update(classToUpdate.data).eq('gym_id', gymId).eq('id', classToUpdate.id)
      );
    }

    if (classesToCreate.length > 0) {
      operations.push(
        supabase.from('scheduled_classes').insert(classesToCreate)
      );
    }

    const results = await Promise.all(operations);
    for (const result of results) {
      if (result.error) throw result.error;
    }

    console.log(`Planning dupliqué: ${classesToCreate.length} créés, ${classesToUpdate.length} mis à jour, ${classesToDelete.length} supprimés`);
  };

  return { duplicateSchedule };
};
